import React from 'react';
import {
  Grid,
  Box,
  Typography,
  FormControl,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Stack,
  Chip} from '@mui/material';
import { grey } from '@mui/material/colors';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { vestResolver } from '@hookform/resolvers/vest';
import { create, test, enforce } from 'vest';
import UICard, { UICardContent, UICardFooter, UICardHeading } from '@/components/Shared/Utils/UICard';
import Utils from '@/components/Shared/Utils';
import { LoadingBackdrop } from '@/components/Layout/DefaultLayout';

// Form validation
const suite = create('reseller_form', (data = {}) => {
  test('name', 'Reseller name is required', () => {
    enforce(data.name).isNotEmpty();
  });

  test('industry', 'Industry is required', () => {
    enforce(data.industry).isNotEmpty();
  });

  test('region', 'Region is required', () => {
    enforce(data.region).isNotEmpty();
  });

  test('contactEmail', 'Email is required', () => {
    enforce(data.contactEmail).isNotEmpty();
  });

  test('contactEmail', 'Email must be valid', () => {
    if (data.contactEmail) {
      enforce(data.contactEmail).matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    }
  });

  test('commissionRate', 'Commission rate is required', () => {
    enforce(data.commissionRate).isNotEmpty();
  });

  test('commissionRate', 'Commission rate must be a number between 1 and 40', () => {
    if (data.commissionRate) {
      const rate = Number(data.commissionRate);
      enforce(rate).greaterThanOrEquals(1);
      enforce(rate).lessThanOrEquals(40);
    }
  });
});

interface ResellerFormValues {
  name: string;
  industry: string;
  region: string;
  status: string;
  tier: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  commissionRate: string;
  paymentTerms: string;
  products: {
    cloudServer: boolean;
    vps: boolean;
    storage: boolean;
    cdn: boolean;
  };
  notes: string;
}

// Mock function to get reseller data for edit mode - replace with API call
const getResellerById = (id: string) => {
  return {
    id,
    name: 'TechPartners Inc.',
    region: 'United States',
    industry: 'Technology',
    status: 'Active',
    tier: 'Premium',
    contactName: 'John Smith',
    contactEmail: 'john.smith@techpartners.com',
    contactPhone: '+1 555-123-4567',
    commissionRate: '25',
    paymentTerms: 'Net 30',
    products: {
      cloudServer: true,
      vps: true,
      storage: true,
      cdn: false
    },
    notes: 'Strategic partner with strong technical capabilities and market presence.'
  };
};

export default function ResellerCreate() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const methods = useForm<ResellerFormValues>({
    defaultValues: {
      name: '',
      industry: '',
      region: '',
      status: 'Pending',
      tier: 'Standard',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      commissionRate: '20',
      paymentTerms: 'Net 30',
      products: {
        cloudServer: false,
        vps: false,
        storage: false,
        cdn: false
      },
      notes: ''
    },
    resolver: vestResolver(suite)
  });
  
  const { control, handleSubmit, formState: { errors }, reset, watch } = methods;

  // If in edit mode, load reseller data
  React.useEffect(() => {
    if (isEditMode && id) {
      const resellerData = getResellerById(id);
      reset({
        name: resellerData.name,
        industry: resellerData.industry,
        region: resellerData.region,
        status: resellerData.status,
        tier: resellerData.tier,
        contactName: resellerData.contactName,
        contactEmail: resellerData.contactEmail,
        contactPhone: resellerData.contactPhone,
        commissionRate: resellerData.commissionRate,
        paymentTerms: resellerData.paymentTerms,
        products: resellerData.products,
        notes: resellerData.notes
      });
    }
  }, [isEditMode, id, reset]);

  const regions = ['United States', 'Singapore', 'Malaysia', 'Indonesia', 'Vietnam', 'Japan', 'South Korea', 'Germany', 'United Kingdom', 'France'];
  const industries = ['Technology', 'Cloud Services', 'IT Services', 'Telecom', 'Consulting', 'E-commerce', 'Finance'];
  const tiers = ['Standard', 'Premium', 'Elite'];
  const statusOptions = ['Active', 'Pending', 'Suspended'];
  const paymentTermsOptions = ['Net 15', 'Net 30', 'Net 45', 'Net 60'];

  const onSubmit = (data: ResellerFormValues) => {
    console.log('Submitted data:', data);
    // API call would go here
    navigate('/resellers');
  };

  const sidebar = React.useMemo(() => ({
    name: '@Resellers',
    uuid: '',
    provider: '',
    paths: [
      {
        name: 'Dashboard',
        key: 'dashboard',
        slug: '/resellers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Customers',
        key: 'customers',
        slug: '/resellers/customers',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Products',
        key: 'products',
        slug: '/resellers/products',
        icon: null,
        disabled: false,
        beta: false,
      },
      {
        name: 'Billing',
        key: 'billing',
        slug: '/resellers/billing',
        icon: null,
        disabled: false,
        beta: false,
      }
    ]
  }), []);

  const resellerProducts = watch('products');

  return (
    <Grid container>
      <Utils.LayoutUtils.MainSidebar {...sidebar} />
      <Utils.LayoutUtils.MainContainer background="white">
        <React.Suspense fallback={<LoadingBackdrop />}>
          <Utils.LayoutUtils.MainWrapper>
            <Stack direction="row" justifyContent="space-between" mb={4}>
              <Box my="auto" width="75%">
                <Utils.LayoutUtils.MainHeading
                  title={isEditMode ? "Edit Reseller" : "Create Reseller"}
                  description="Create and manage your resellers, including region, industry, and product authorizations."
                />
              </Box>
              <Stack my="auto" width="25%" direction="row" justifyContent="end">
                <Utils.FormUtils.PrimaryButton
                  component={NavLink}
                  to="/resellers"
                  variant="outlined"
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Utils.FormUtils.PrimaryButton>
                <Utils.FormUtils.PrimaryButton
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                >
                  {isEditMode ? "Save Changes" : "Create Reseller"}
                </Utils.FormUtils.PrimaryButton>
              </Stack>
            </Stack>
            
            <Grid container spacing={2}>
              <Grid item md={8} xs={12}>
                <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                  <FormProvider {...methods}>
                    <UICard gradient>
                      <UICardHeading
                        title="Reseller Information"
                        description="Enter basic information about the reseller"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Typography fontSize={12} sx={{ mb: 0.5 }}>
                            Reseller Name
                          </Typography>
                          <Controller
                            control={control}
                            name="name"
                            render={({ field }) => (
                              <Utils.FormUtils.PrimaryInput
                                {...field}
                                fullWidth
                                size="small"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                              />
                            )}
                          />
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Reseller Location and Industry"
                        description="Specify the reseller's region and industry sector"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Region
                              </Typography>
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="region"
                                  render={({ field }) => (
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                      error={!!errors.region}
                                    >
                                      {regions.map((region) => (
                                        <Utils.FormUtils.PrimarySelectItem key={region} value={region}>
                                          {region}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  )}
                                />
                                {errors.region && (
                                  <FormHelperText error>{errors.region.message}</FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Industry
                              </Typography>
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="industry"
                                  render={({ field }) => (
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                      error={!!errors.industry}
                                    >
                                      {industries.map((industry) => (
                                        <Utils.FormUtils.PrimarySelectItem key={industry} value={industry}>
                                          {industry}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  )}
                                />
                                {errors.industry && (
                                  <FormHelperText error>{errors.industry.message}</FormHelperText>
                                )}
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Reseller Status and Tier"
                        description="Set the reseller's status and partnership tier"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Status
                              </Typography>
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="status"
                                  render={({ field }) => (
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                    >
                                      {statusOptions.map((status) => (
                                        <Utils.FormUtils.PrimarySelectItem key={status} value={status}>
                                          {status}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  )}
                                />
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Tier
                              </Typography>
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="tier"
                                  render={({ field }) => (
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                    >
                                      {tiers.map((tier) => (
                                        <Utils.FormUtils.PrimarySelectItem key={tier} value={tier}>
                                          {tier}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Contact Information"
                        description="Reseller primary contact details"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Contact Name
                              </Typography>
                              <Controller
                                control={control}
                                name="contactName"
                                render={({ field }) => (
                                  <Utils.FormUtils.PrimaryInput
                                    {...field}
                                    fullWidth
                                    size="small"
                                    placeholder="Full name of primary contact"
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Contact Email
                              </Typography>
                              <Controller
                                control={control}
                                name="contactEmail"
                                render={({ field }) => (
                                  <Utils.FormUtils.PrimaryInput
                                    {...field}
                                    fullWidth
                                    size="small"
                                    error={!!errors.contactEmail}
                                    helperText={errors.contactEmail?.message}
                                    placeholder="email@example.com"
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Contact Phone
                              </Typography>
                              <Controller
                                control={control}
                                name="contactPhone"
                                render={({ field }) => (
                                  <Utils.FormUtils.PrimaryInput
                                    {...field}
                                    fullWidth
                                    size="small"
                                    error={!!errors.contactPhone}
                                    helperText={errors.contactPhone?.message}
                                    placeholder="+1 123-456-7890"
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Commercial Terms"
                        description="Commission rates and payment terms"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Commission Rate (%)
                              </Typography>
                              <Controller
                                control={control}
                                name="commissionRate"
                                render={({ field }) => (
                                  <Utils.FormUtils.PrimaryInput
                                    {...field}
                                    fullWidth
                                    size="small"
                                    type="number"
                                    error={!!errors.commissionRate}
                                    helperText={errors.commissionRate?.message}
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography fontSize={12} sx={{ mb: 0.5 }}>
                                Payment Terms
                              </Typography>
                              <FormControl fullWidth>
                                <Controller
                                  control={control}
                                  name="paymentTerms"
                                  render={({ field }) => (
                                    <Utils.FormUtils.PrimarySelect
                                      {...field}
                                      fullWidth
                                      size="small"
                                    >
                                      {paymentTermsOptions.map((term) => (
                                        <Utils.FormUtils.PrimarySelectItem key={term} value={term}>
                                          {term}
                                        </Utils.FormUtils.PrimarySelectItem>
                                      ))}
                                    </Utils.FormUtils.PrimarySelect>
                                  )}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Products"
                        description="Select products this reseller is authorized to sell"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Grid container>
                            <Grid item xs={12} md={6}>
                              <Controller
                                control={control}
                                name="products.cloudServer"
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    componentsProps={{
                                      typography: {
                                        sx: {
                                          fontSize: 13
                                        }
                                      }
                                    }}
                                    control={<Checkbox size="small" checked={value} onChange={onChange} />}
                                    label="Cloud Server"
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                control={control}
                                name="products.vps"
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    componentsProps={{
                                      typography: {
                                        sx: {
                                          fontSize: 13
                                        }
                                      }
                                    }}
                                    control={<Checkbox size="small" checked={value} onChange={onChange} />}
                                    label="VPS"
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                control={control}
                                name="products.storage"
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    componentsProps={{
                                      typography: {
                                        sx: {
                                          fontSize: 13
                                        }
                                      }
                                    }}
                                    control={<Checkbox size="small" checked={value} onChange={onChange} />}
                                    label="Storage"
                                  />
                                )}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Controller
                                control={control}
                                name="products.cdn"
                                render={({ field: { value, onChange } }) => (
                                  <FormControlLabel
                                    componentsProps={{
                                      typography: {
                                        sx: {
                                          fontSize: 13
                                        }
                                      }
                                    }}
                                    control={<Checkbox size="small" checked={value} onChange={onChange} />}
                                    label="CDN"
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </UICardContent>
                    </UICard>

                    <UICard gradient>
                      <UICardHeading
                        title="Additional Notes"
                        description="Notes and comments about this reseller"
                      />
                      <UICardContent>
                        <Box p={2}>
                          <Controller
                            control={control}
                            name="notes"
                            render={({ field }) => (
                              <Utils.FormUtils.PrimaryInput
                                {...field}
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="Enter notes about this reseller"
                                error={!!errors.notes}
                                helperText={errors.notes?.message}
                              />
                            )}
                          />
                        </Box>
                      </UICardContent>
                    </UICard>
                  </FormProvider>
                </Box>
              </Grid>
              
              <Grid item md={4} xs={12}>
                <UICard width={360} gradient>
                  <UICardHeading
                    title="Reseller Summary"
                    description="Review the reseller configuration before saving."
                  />
                  <UICardContent>
                    <Box p={2}>
                      <Typography fontSize={12} color={grey[600]}>
                        Reseller Name
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('name') || 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Region
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('region') || 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Industry
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('industry') || 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Status
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('status') || 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Tier
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('tier') || 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Commission Rate
                      </Typography>
                      <Typography fontSize={14} fontWeight={500} mb={2}>
                        {watch('commissionRate') ? `${watch('commissionRate')}%` : 'Not specified'}
                      </Typography>
                      
                      <Typography fontSize={12} color={grey[600]}>
                        Products
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" mb={2}>
                        {resellerProducts?.cloudServer && <Chip label="Cloud Server" size="small" sx={{ mt: 0.5 }} />}
                        {resellerProducts?.vps && <Chip label="VPS" size="small" sx={{ mt: 0.5 }} />}
                        {resellerProducts?.storage && <Chip label="Storage" size="small" sx={{ mt: 0.5 }} />}
                        {resellerProducts?.cdn && <Chip label="CDN" size="small" sx={{ mt: 0.5 }} />}
                        {!Object.values(resellerProducts || {}).some(v => v) && (
                          <Typography fontSize={14}>No products selected</Typography>
                        )}
                      </Stack>
                    </Box>
                  </UICardContent>
                  <UICardFooter>
                    <Box p={2}>
                      <Stack direction="row" spacing={1}>
                        <Utils.ActionButton
                          text="Cancel"
                          light
                          onClick={() => navigate('/resellers')}
                          sx={{ flex: 1, fontSize: 13 }}
                        />
                        <Utils.ActionButton
                          text={isEditMode ? "Update" : "Create"}
                          onClick={handleSubmit(onSubmit)}
                          sx={{ flex: 1, fontSize: 13 }}
                        />
                      </Stack>
                    </Box>
                  </UICardFooter>
                </UICard>
              </Grid>
            </Grid>
          </Utils.LayoutUtils.MainWrapper>
        </React.Suspense>
      </Utils.LayoutUtils.MainContainer>
    </Grid>
  );
} 